// Change a task's planned dates (spec J3, plan §4 PATCH /tasks/:taskId/plan). The supervisor works
// within the task's phase and the owner anywhere; the API decides, and its refusal is shown here.
// An empty date, or "Go back to the pack dates", clears the re-planned date.
import { useId, useState, type FormEvent } from 'react';
import type { TaskDetail } from '@cis/api-client';
import type { TaskWrite } from '../../api/tasks.ts';
import { Modal } from '../../ui/modal.tsx';
import { formatDate } from './format.ts';
import { DialogActions, ProblemText } from './dialog-parts.tsx';
import { baseFor, type TaskWriter } from './use-task-writer.ts';

type Replan = Extract<TaskWrite, { kind: 'replan' }>;

const edited = (d: TaskDetail): unknown => [d.state.replannedStart, d.state.replannedEnd];

const shown = (d: TaskDetail) => ({
  start: d.state.replannedStart ?? d.plannedStart ?? '',
  end: d.state.replannedEnd ?? d.plannedEnd ?? '',
});

/** Only the dates the person changed; '' clears the re-planned date. */
export function replanOf(detail: TaskDetail, start: string, end: string): Replan | null {
  const before = shown(detail);
  const value = (now: string, was: string): string | null | undefined => {
    if (now === was) return undefined;
    return now === '' ? null : now;
  };
  const write: Replan = {
    kind: 'replan',
    start: value(start, before.start),
    end: value(end, before.end),
  };
  return write.start === undefined && write.end === undefined ? null : write;
}

function PhaseHint(props: { readonly detail: TaskDetail }): React.JSX.Element {
  const { detail } = props;
  const { code, start, end } = detail.phase;
  return (
    <p className="modal__body">
      Phase {code} runs from {formatDate(start ?? null)} to {formatDate(end ?? null)}. The pack
      plans this task from {formatDate(detail.plannedStart)} to {formatDate(detail.plannedEnd)}. The
      supervisor can move it within the phase; only the owner can move it outside.
    </p>
  );
}

function DateField(props: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {props.label}
      </label>
      <input
        id={id}
        type="date"
        className="field__input"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </div>
  );
}

export function DatesDialog(props: {
  readonly detail: TaskDetail;
  readonly writer: TaskWriter;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { detail, writer } = props;
  const [opened] = useState(detail);
  const [start, setStart] = useState(shown(opened).start);
  const [end, setEnd] = useState(shown(opened).end);
  const [problem, setProblem] = useState<string | null>(null);
  const replanned = opened.state.replannedStart !== null || opened.state.replannedEnd !== null;
  const send = async (write: Replan | null): Promise<void> => {
    if (write === null) {
      setProblem('Change a date first, or press Cancel.');
      return;
    }
    const outcome = await writer.send(write, baseFor(opened, detail, edited));
    if (outcome === 'ok' || outcome === 'conflict') props.onClose();
    if (outcome === 'failed') setProblem(writer.lastError());
  };
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (start !== '' && end !== '' && start > end) {
      setProblem('The start date must be on or before the end date.');
      return;
    }
    void send(replanOf(opened, start, end));
  };
  return (
    <Modal title="Change dates" onClose={props.onClose} busy={writer.pending}>
      <PhaseHint detail={opened} />
      <form className="field" onSubmit={onSubmit}>
        <div className="form-row">
          <DateField label="Start" value={start} onChange={setStart} />
          <DateField label="End (due date)" value={end} onChange={setEnd} />
        </div>
        <ProblemText problem={problem} />
        <DialogActions writer={writer} onCancel={props.onClose} submitLabel="Save dates">
          {replanned && (
            <button
              type="button"
              className="btn btn--secondary"
              disabled={writer.pending}
              onClick={() => void send({ kind: 'replan', start: null, end: null })}
            >
              Go back to the pack dates
            </button>
          )}
        </DialogActions>
      </form>
    </Modal>
  );
}
